import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

export default function PendingAccessRequests({ onApproved }) {
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/admin/access-requests`, cfg).then(r => setReqs(r.data)).catch(() => {});
  }, []);

  const act = async (r, approve) => {
    if (!approve && !window.confirm(`Deny access for ${r.email}?`)) return;
    setBusy(r.id);
    try {
      if (approve) {
        const { data } = await axios.post(`${BACKEND}/api/admin/access-requests/${r.id}/approve`, {}, cfg);
        onApproved(data);
        toast[data.welcome_status === "sent" ? "success" : "warning"](
          data.welcome_status === "sent" ? `Approved ${r.email}. Welcome email sent.` : `Approved ${r.email}, but the welcome email ${data.welcome_status === "skipped" ? "was skipped (SMTP not configured)" : "failed to send"}.`);
      } else {
        const { data } = await axios.delete(`${BACKEND}/api/admin/access-requests/${r.id}`, cfg);
        toast.success(data.email_status === "sent" ? `Denied ${r.email}. They've been notified by email.` : `Denied ${r.email} (notice email ${data.email_status === "skipped" ? "skipped" : "failed to send"}).`);
      }
      setReqs(prev => prev.filter(x => x.id !== r.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
    setBusy(null);
  };

  if (!reqs.length) return null;
  return (
    <div className="border-b border-bt-border-subtle" data-testid="pending-access-requests">
      {reqs.map(r => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-bt-border-subtle bg-bt-gold/5" data-testid={`access-request-${r.email}`}>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono border border-bt-gold/40 text-bt-gold"><Clock className="w-3 h-3" />PENDING</span>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-sm text-bt-text-primary">{r.email} <span className="text-bt-text-tertiary">· {r.app_name}</span></p>
            <p className="text-xs text-bt-text-tertiary font-body">{r.real_name} · found us via {r.source}{r.source_detail ? ` (${r.source_detail})` : ""} · {new Date(r.created_at).toLocaleString()}</p>
          </div>
          <Button size="sm" disabled={busy === r.id} onClick={() => act(r, true)} data-testid={`approve-request-${r.email}`}
            className="bg-bt-gold/15 border border-bt-gold/40 text-bt-gold hover:bg-bt-gold/25">
            {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Approve &amp; Send Welcome
          </Button>
          <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => act(r, false)} data-testid={`deny-request-${r.email}`}
            className="text-bt-negative hover:bg-bt-negative/10"><X className="w-3.5 h-3.5 mr-1" />Deny</Button>
        </div>
      ))}
    </div>
  );
}
